import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  reload,
  onAuthStateChanged,
} from "firebase/auth";

function configReady() {
  return Boolean(
    process.env.REACT_APP_FIREBASE_API_KEY &&
      process.env.REACT_APP_FIREBASE_AUTH_DOMAIN &&
      process.env.REACT_APP_FIREBASE_PROJECT_ID &&
      process.env.REACT_APP_FIREBASE_APP_ID
  );
}

let app = null;
let auth = null;
let authReadyPromise = null;

export function getFirebaseAuth() {
  if (!configReady()) return null;
  if (!app) {
    app =
      getApps()[0] ??
      initializeApp({
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        appId: process.env.REACT_APP_FIREBASE_APP_ID,
      });
  }
  if (!auth) auth = getAuth(app);
  return auth;
}

/** Wait until Firebase restores currentUser from persistence (fixes reload 401 races). */
export function waitForFirebaseAuth(timeoutMs = 8000) {
  const a = getFirebaseAuth();
  if (!a) return Promise.resolve(null);
  if (a.currentUser) return Promise.resolve(a.currentUser);
  if (!authReadyPromise) {
    authReadyPromise = new Promise((resolve) => {
      let settled = false;
      const finish = (user) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        try {
          unsub();
        } catch {
          /* already unsubscribed */
        }
        authReadyPromise = null;
        // Prefer live currentUser in case the callback raced with restore.
        resolve(user ?? a.currentUser ?? null);
      };
      const timer = setTimeout(() => finish(a.currentUser), timeoutMs);
      const unsub = onAuthStateChanged(a, (user) => finish(user));
    });
  }
  return authReadyPromise;
}

export async function firebaseEmailLogin(email, password) {
  const a = getFirebaseAuth();
  if (!a) throw new Error("Firebase client not configured");
  const cred = await signInWithEmailAndPassword(a, email, password);
  return cred.user.getIdToken(true);
}

export async function firebaseEmailSignup(email, password) {
  const a = getFirebaseAuth();
  if (!a) throw new Error("Firebase client not configured");
  const cred = await createUserWithEmailAndPassword(a, email, password);
  return cred.user.getIdToken(true);
}

export async function firebaseForgotPassword(email) {
  const a = getFirebaseAuth();
  if (!a) throw new Error("Firebase client not configured");
  const continueUrl =
    process.env.REACT_APP_PASSWORD_RESET_CONTINUE_URL ||
    (typeof window !== "undefined" ? `${window.location.origin}/login` : undefined);
  await sendPasswordResetEmail(a, email, continueUrl ? { url: continueUrl } : undefined);
}

export async function firebaseSendVerificationEmail() {
  const a = getFirebaseAuth();
  if (!a?.currentUser) throw new Error("Not signed in");
  await sendEmailVerification(a.currentUser);
}

/** Reload Firebase user and return a fresh ID token (picks up email_verified). */
export async function firebaseReloadIdToken() {
  const a = getFirebaseAuth();
  if (!a?.currentUser) throw new Error("Not signed in");
  await reload(a.currentUser);
  return a.currentUser.getIdToken(true);
}

export function isFirebaseClientConfigured() {
  return configReady();
}

/** Map Firebase Auth errors to readable login copy. */
export function mapFirebaseAuthError(err) {
  const code = err?.code || "";
  const message = err instanceof Error ? err.message : String(err || "Login failed");
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
    case "auth/invalid-email":
      return "Invalid email or password.";
    case "auth/too-many-requests":
      return "Too many attempts. Try again later or reset your password.";
    case "auth/user-disabled":
      return "This account has been disabled. Contact your admin.";
    case "auth/network-request-failed":
      return "Network error reaching Firebase. Check your connection.";
    case "auth/unauthorized-domain":
      return "This domain is not authorized in Firebase Auth settings.";
    default:
      if (/Firebase: Error/i.test(message) && code) {
        return `Firebase auth failed (${code}).`;
      }
      return message || "Login failed";
  }
}
